import { Link } from "react-router-dom";
import { LegalLayout, H2, H3, P, List, Note } from "./LegalLayout";
import { FOUND_CONFIG, contactTarget } from "./foundConfig";

export default function Privacy() {
  const c = FOUND_CONFIG;
  const pc = contactTarget("privacy");

  return (
    <LegalLayout
      title="FOUND Privacy Policy"
      description="How FOUND, operated by Dream Big Entertainment Ltd, collects, uses, stores and protects your information."
    >
      <P>
        This Privacy Policy explains how <strong>{c.company}</strong> (“we”, “us”, “Dream Big”)
        collects, uses, and protects information in connection with <strong>FOUND</strong>, our
        music discovery social platform (“FOUND”, the “Service”). By using FOUND you agree to the
        practices described here.
      </P>
      <P>
        <strong>Effective date:</strong> {c.effectiveDate} · <strong>Last updated:</strong> {c.lastUpdated}
      </P>

      <H2>1. Information you provide</H2>
      <List items={[
        "Account information — such as email address, login credentials and account type (Artist or Scout).",
        "Username and profile information — display name, bio, profile image and links you choose to add.",
        "Artist information — details Artists add about themselves, their releases and their work.",
        "Music and video uploads — audio and video files Artists upload to release on FOUND.",
        "Artwork — cover art and images associated with releases or profiles.",
        "Comments and other user-generated content — including Scout Posts and messages you submit.",
      ]} />

      <H2>2. Information created through your activity</H2>
      <List items={[
        "Scout activity — the Artists and releases you discover, pick and interact with.",
        "Likes, saves and follows — signals you give to music, Artists and other users.",
        "Watch and listening behaviour — plays, listens, skips and completion used to measure how a release performs.",
        "FOUND FIRST and reputation data — your discovery history, Scout Points, Successful Picks and Breakout Picks.",
      ]} />

      <H2>3. Device, technical and analytics information</H2>
      <List items={[
        "Device and technical information — such as device type, operating system, app version, language and general diagnostics.",
        "Analytics — aggregated and event-level usage data used to understand and improve how FOUND is used.",
        "Notifications — if you enable them, tokens/settings used to send you relevant updates.",
      ]} />

      <H2>4. Reports and moderation information</H2>
      <P>
        When you report content or an account, or when your content is reviewed, we process the
        report details, the content in question and the outcome (for example whether content is
        approved, flagged or removed) to keep FOUND safe.
      </P>

      <H2>5. Why we process information</H2>
      <List items={[
        "Providing FOUND and creating and maintaining your account.",
        "Delivering the discovery feed and recommendations.",
        "Measuring release performance (TESTING, GROWING, RISING, BREAKOUT).",
        "Operating the Scout system, FOUND FIRST, Scout Points and reputation.",
        "Producing Artist analytics and audience insights.",
        "Safety and moderation, and preventing fraud, abuse and manipulation.",
        "Improving FOUND and providing customer support.",
      ]} />

      <H2>6. Service providers and third parties</H2>
      <P>
        We use trusted service providers to operate FOUND. They may process information on our
        behalf only to provide their services to us and are required to protect it. FOUND
        currently uses:
      </P>
      <List items={c.serviceProviders.map((s) => `${s.name} — ${s.purpose}`)} />
      <Note>
        We will update this section as additional providers are integrated. We do not list
        analytics vendors, advertising SDKs, payment processors or other third parties that have
        not actually been integrated into FOUND.
      </Note>
      <P>
        <strong>We do not sell your personal information.</strong>
      </P>

      <H2>7. Data storage and international processing</H2>
      <P>
        Information is stored using our backend infrastructure providers listed above. Depending on
        where those providers operate, information may be processed in countries other than your
        own. Where required, we take steps to ensure appropriate safeguards for international
        transfers.
      </P>

      <H2>8. Data retention</H2>
      <P>
        We keep information for as long as needed to provide FOUND and for the purposes described in
        this policy, after which it is deleted or anonymised. Some information may be retained
        longer where required for legal obligations, security, fraud prevention or dispute
        resolution.
      </P>

      <H2>9. Account deletion</H2>
      <P>
        You can request deletion of your FOUND account and associated personal data. See{" "}
        <Link to="/found/delete-account" className="text-white underline">Delete Your FOUND Account</Link>{" "}
        for details on what is removed and what may be retained.
      </P>

      <H2>10. Your privacy rights</H2>
      <P>
        Depending on your location, you may have rights to access, correct, delete, export or
        restrict the processing of your personal information, and to object to certain processing.
        To exercise these rights, contact us using the details below.
      </P>

      <H2>11. Security</H2>
      <P>
        We use technical and organisational measures designed to protect information. No method of
        transmission or storage is completely secure, but we work to protect your information and to
        respond appropriately to any incidents.
      </P>

      <H2>12. Children's privacy</H2>
      <P>
        FOUND is not directed to children under the age required by your local laws (and in any case
        not under 13). We do not knowingly collect personal information from children below the
        applicable age. If you believe a child has provided information, contact us so we can take
        appropriate action.
      </P>

      <H2>13. Changes to this policy</H2>
      <P>
        We may update this policy from time to time. When we do, we will revise the “Last updated”
        date above and, where appropriate, provide additional notice within FOUND.
      </P>

      <H2>14. Contact</H2>
      <P>
        Questions about privacy? {pc.isEmail
          ? <>Email us at <a href={pc.href} className="text-white underline">{pc.href.replace("mailto:", "")}</a>.</>
          : <>Please reach us through the <Link to="/found/support" className="text-white underline">FOUND Support</Link> page.</>}
        {" "}You can also write to {c.company}, {c.location}.
      </P>
    </LegalLayout>
  );
}
